import {defineStore} from "pinia";
import * as yachtService from "@/services/yacht.service";
import * as LocationService from "@/services/location.service";
import * as DateUtils from "@/helpers/dateutils";
import {Yacht} from "@/models/Yacht";
import {Offer} from "@/models/Offer";
import {computed, nextTick, ref, watch} from "vue";
import moment from "moment/moment";
import {useUserStore} from "@/stores/userStore";
import {ILocation} from "@/models/Location";
import * as CountryService from "@/services/country.service";
import * as RegionService from "@/services/region.service";
import * as CityService from "@/services/city.service";
import {hasAny} from "v-calendar/dist/types/src/utils/helpers";
import { useRouter, useRoute } from "vue-router";


export const useYachtStore = defineStore('yatchs', () => {

    const userStore = useUserStore();
    const state = ref('idle');
    const booked = ref(false);
    const slug = ref('');
    const location = ref<ILocation>()
    const router = useRouter();
    const route = useRoute();
    const equipmentShown = ref(5);
    const equipmentShow = ref(false);
    const yacht = ref<Partial<Yacht>>()
    const selections = ref({
        dateFrom: new Date(),
        dateTo: new Date(),
        persons: 0
    })

    const dateDays = ref(1)
    const availabilities = ref([])
    const selectedAmenities = ref([])


    const yachtLocation = computed({
        get() {
            const availability = (availabilities.value[1] && availabilities.value[1].location) || {};

            console.log("Location", availability);

            return {
                name: availability.name,
                lat: availability.latitude || 0,
                lng: availability.longitude || 0,
                country: availability.country
            };
        },
        set(newValue) {
            if (!availabilities.value[1]) {
                return;
            }

            if (!availabilities.value[1].location) {
                availabilities.value[1].location = {};
            }

            availabilities.value[1].location.name = newValue.name;
            availabilities.value[1].location.latitude = newValue.lat;
            availabilities.value[1].location.longitude = newValue.lng;

            console.log("Updated location", availabilities.value[1].location);
        }
    });
    
    
    const mandatoryAmenities = computed(() => {
        let amenities = []
        if (availabilities.value && availabilities.value.length > 2) {
            amenities = availabilities.value[1].amenities
        }

        if (amenities.length <= 0) {
            return []
        }
        return amenities.filter(a => a.basic === 'composite' && a.obligatory)
    })

    const optionalAmenities = computed(() => {
        let amenities = []
        if (availabilities.value && availabilities.value.length > 2) {
            amenities = availabilities.value[1].amenities
        }

        if (amenities.length <= 0) {
            return []
        }

        return amenities.filter(a => a.basic === 'composite' && !a.obligatory)
    })

    const equipment = computed(() => {
        let amenities = []
        if (availabilities.value && availabilities.value.length > 2) {
            amenities = availabilities.value[1].amenities
        }

        if (amenities.length <= equipmentShown.value) {
            return {
                'show': amenities,
                'hide': [],
                'hidden': 0,
                'total': amenities.length,
            }
        }

        const equipment = amenities.filter((amenity) => amenity.basic == 'basic')
        return {
            'show': equipment.slice(0, equipmentShown.value),
            'hide': equipment.slice(equipmentShown.value, equipment.length),
            'hidden': equipment.length - equipmentShown.value,
            'total': equipment.length,
        }
    })

    const selectedAvailabilityDatesText = computed(() => {
        return `${userStore.formatDate(selections.value.dateFrom)} – ${userStore.formatDate(selections.value.dateTo)}`
    })

    const availabilityDatesText = (availability)=> {
        return `${userStore.formatDate(availability.dateFrom)} – ${userStore.formatDate(availability.dateTo)}`
    }
    const availabilityState = computed(() => {

        console.log("Loading ",
            availabilities.value,
            availabilities.value.length,
            availabilities.value.find((a)=>{ a['state'] =='loading' }))

        const loadingAvailabilites = availabilities.value.find((a)=>{ a['state'] =='loading' })

        return loadingAvailabilites && loadingAvailabilites.length > 0 ? 'loading' : 'idle';


    })
    const hasAvailabilities = computed(() =>  availabilities.value.filter((a)=> 'available' in a && a['available']).length)


    const priceDetails = computed(() => {
        if (availabilities.value.length < 1) {
            return {
                'basePrice': 0.0,
                'offerPrice': 0.0,
                'hasOffer': false,
                'total': 0.0,
                'state':'idle',
                'amenities': {
                    'advance': [],
                    'separate': []
                }
            }
        }


        let total = availabilities.value[1]['offerPrice']

        if (total <= 0) {
            return {
                'basePrice': 0.0,
                'offerPrice': 0.0,
                'hasOffer': false,
                'state':'idle',
                'total': 0.0,
                'amenities': {
                    'advance': [],
                    'separate': []
                }
            }
        }

        let amenities = []
        if (availabilities.value && availabilities.value.length > 2) {
            amenities = availabilities.value[1].amenities
        }

        const mandatory = amenities.filter(a => a.basic == 'composite' && a.obligatory)

        const optional = amenities.filter(a => a.basic == 'composite'
            && !a.obligatory
            && selectedAmenities.value.find((id) => id == a.id))


        const computeAmenityPrice = (amenity) => {
            if (!amenity.amenityUnit) {
                return amenity.price
            }

            if (!amenity.amenityUnit.computable ||
                amenity.amenityUnit.unitCategory.name == "Unit") {
                return amenity.price
            }
            if (amenity.amenityUnit.unitCategory.name == "People") {
                return amenity.price * selections.value.persons
            }
            if (amenity.amenityUnit.unitCategory.name == "Time") {
                return ((amenity.price / amenity.amenityUnit.ratio) * dateDays.value)
            }

            if (amenity.amenityUnit.unitCategory.name == "Time Person") {

                return ((amenity.price / amenity.amenityUnit.ratio) * dateDays.value)
                    * selections.value.persons
            }

            return amenity.price
        }

        const computedAmentities = {
            'separate': [],
            'advance': [],
        }
        let totalAdvanced = 0;
        let totalOnSpot = 0;

        mandatory.forEach((amenity) => {

            const computedPrice = computeAmenityPrice(amenity)
            if (amenity.paymentType == 'advance' || computedPrice === 0) {
                computedAmentities.advance.push({
                    'id': amenity.id,
                    'name': amenity.name,
                    'subtotal': computedPrice,
                })
                totalAdvanced = totalAdvanced + amenity.price
            } else {
                computedAmentities.separate.push({
                    'id': amenity.id,
                    'name': amenity.name,
                    'subtotal': computedPrice,
                })
                totalOnSpot = totalOnSpot + computedPrice
            }

        })

        optional.forEach((amenity) => {

            const computedPrice = computeAmenityPrice(amenity)
            if (amenity.paymentType == 'advance' || computedPrice === 0) {
                computedAmentities.advance.push({
                    'id': amenity.id,
                    'name': amenity.name + " / " + amenity.amenityUnit?.uomName,
                    'subtotal': computedPrice,
                })
                totalAdvanced = totalAdvanced + computedPrice

            } else {
                computedAmentities.separate.push({
                    'id': amenity.id,
                    'name': amenity.name + " / " + amenity.amenityUnit?.uomName,
                    'subtotal': computedPrice,
                })
                totalOnSpot = totalOnSpot + computedPrice

            }
        })

        return {
            'basePrice': availabilities.value[1]['basePrice'],
            'offerPrice': availabilities.value[1]['offerPrice'],
            'hasOffer': availabilities.value[1]['basePrice'] != availabilities.value[1]['offerPrice'],
            'total': availabilities.value[1]['offerPrice'] + totalAdvanced,
            'totalAdvanced': totalAdvanced,
            'totalOnSpot': totalOnSpot,
            'amenities': computedAmentities
        }


    })


    const canMoveToPreviousPeriod = computed(() => {
        if (availabilities.value.length <= 0) {
            return false
        }
        return availabilities.value[0].dateFrom > new Date()
    })

    const passengers = computed(() => {
        return [...Array(Number(yacht.value.berths)).keys()].map(i => i + 1).filter(i => i <= yacht.value.berths)
    })

    const offer = computed((): Offer => null)

    const handleRoute = async (route) => {
        state.value = 'loading'
        const res = await Promise.all([
            LocationService.searchByIds(route.query.locationIds),
            CountryService.searchByIds(route.query.countryIds || []),
            RegionService.searchByIds(route.query.regionIds || []),
            CityService.searchByIds(route.query.cityIds || []),
            yachtService.get(route.params.id)
        ])

        if( res[0]['data'] && res[0]['data']._embedded && res[0].data._embedded.locations ){
            location.value = res[0].data._embedded.locations[0]
        }

        if( res[1]['data'] && res[1]['data']._embedded && res[1].data._embedded.countries ){
            location.value = res[1].data._embedded.countries[0]
        }

        if( res[2]['data'] && res[2]['data']._embedded && res[2].data._embedded.regions ){
            location.value = res[2].data._embedded.regions[0]
        }

        if( res[3]['data'] && res[3]['data']._embedded && res[3].data._embedded.cities ){
            location.value = res[3].data._embedded.cities[0]
        }

        yacht.value = res[4].data

        let dateStart = moment().isoWeekday(6).toDate()
        let dateEnd = moment().isoWeekday(6).add(7, 'days').toDate()
        let persons = 0

        if (route.query.dateFrom && route.query.dateTo) {
            dateStart = DateUtils.toDate(route.query.dateFrom)
            dateEnd = DateUtils.toDate(route.query.dateTo)
        }

        if (route.query.passengers) {
            persons = parseInt(route.query.passengers);
        }

        if (route.query.skipper && route.query.skipper === "true") {
            persons = persons + 1
        }

        state.value = 'idle'
        await nextTick()
        selections.value = {
            dateFrom: dateStart,
            dateTo: dateEnd,
            persons: persons
        }
    }

    const updatePageUrl = () => {
        router.replace({
            path: route.path,
            query: {
                ...route.query,
                dateFrom: DateUtils.format(selections.value.dateFrom),
                dateTo: DateUtils.format(selections.value.dateTo),
                passengers: selections.value.persons,
            }
        });
    };

    const fetch = () => {
        if(availabilityState.value == 'loading'){
            return
        }
        state.value = "loading";

        const prev = {
            basePrice: 0.0,
            offerPrice: 0.0,
            available: false,
            location: false,
            amenities: [],
            state: "loading",
            ...(DateUtils.previousPeriods(
                selections.value.dateFrom,
                selections.value.dateTo)[0])
        };

        const selected = {
            basePrice: 0.0,
            offerPrice: 0.0,
            available: false,
            location: false,
            amenities: [],
            state: "loading",
            dateFrom: selections.value.dateFrom,
            dateTo: selections.value.dateTo
        };

        const next = {
            basePrice: 0.0,
            offerPrice: 0.0,
            location: 0.0,
            amenities: [],
            available: false,
            state: "loading",
            ...(DateUtils.nextPeriods(
                selections.value.dateFrom,
                selections.value.dateTo, 1)[0])
        }


        availabilities.value = [
            prev,
            selected,
            next]


        dateDays.value = DateUtils.computeDaysBetween(
            selections.value.dateFrom,
            selections.value.dateTo
        )

        let pendingRequests = availabilities.value.length;

        availabilities.value.forEach((availability) => {

            availability['state']='loading';
            yachtService.searchAvailabilities({
                'dateFrom': availability.dateFrom,
                'dateTo': availability.dateTo,
                'persons': selections.value.persons,
                'yachtIds': yacht.value.id,
            }).then((response)=>{
                const yachtResponse = response?.data?.pagedModel?._embedded?.availabilityResponses?.[0];

                if (!yachtResponse) {
                    availability['available'] = false;
                    availability['state'] = 'idle';
                    pendingRequests--;
                    if (pendingRequests === 0) {
                        state.value = "idle";
                    }
                    return;
                }

                availability['available'] = true;
                availability['amenities'] = yachtResponse.yacht.amenities;
                if (yachtResponse.locationFrom) {
                    availability['location'] = {
                        id: yachtResponse.locationFrom.id,
                        name: yachtResponse.locationFrom.name,
                        latitude: yachtResponse.locationFrom.latitude,
                        longitude: yachtResponse.locationFrom.longitude,
                        country: yachtResponse.locationFrom.country?.name
                    };
                }

                availability['basePrice'] = yachtResponse.price;
                availability['offerPrice'] = yachtResponse.finalPrice;
                availability['discount'] = yachtResponse.discount?.[0] || 0;
            }).catch((err) => {
                if (err.response?.status !== 400) {
                    console.error(err);
                }
                availability['available'] = false;
            }).finally(()=>{
                availability['state']='idle';
                pendingRequests--;
                if (pendingRequests === 0) {
                    state.value = "idle";
                }
            })
        })

    }


    const moveToPreviousPeriod = () => {


        if (availabilities.value.length <= 0 || availabilityState.value == 'loading') {
            return
        }

        selections.value.dateFrom = availabilities.value[0].dateFrom
        selections.value.dateTo = availabilities.value[0].dateTo
    }


    const moveToNextPeriod = () => {

        if (availabilities.value.length <= 0 || availabilityState.value == 'loading') {
            return
        }

        selections.value.dateFrom = availabilities.value[availabilities.value.length - 1].dateFrom
        selections.value.dateTo = availabilities.value[availabilities.value.length - 1].dateTo
    }
    const selectAvailability = (index) => {

        if (!availabilities.value || !availabilities.value.length || index == 1) {
            return
        }

        selections.value.dateFrom = availabilities.value[index].dateFrom
        selections.value.dateTo = availabilities.value[index].dateTo

    }


    return {

        state,
        booked,
        slug,
        location,
        equipmentShown,
        equipmentShow,
        yacht,
        selections,
        dateDays,
        availabilities,
        selectedAmenities,
        canMoveToPreviousPeriod,
        passengers,
        offer,
        mandatoryAmenities,
        optionalAmenities,
        equipment,
        priceDetails,
        selectedAvailabilityDatesText,
        availabilityDatesText,
        availabilityState,
        hasAvailabilities,
        fetch,
        updatePageUrl,
        moveToPreviousPeriod,
        moveToNextPeriod,
        selectAvailability,
        handleRoute,
        yachtLocation,
    }


});
